import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users, UserPlus, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getTeam, inviteMember, inviteLink } from "@/lib/dentiumApi";
import { SettingsCard } from "@/components/settings/SettingsCard";
import { TeamMemberRow, copyText } from "@/components/settings/TeamMemberRow";

export const TeamPanel = ({ currentUser }) => {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("recepcion");
  const [lastLink, setLastLink] = useState("");
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: getTeam });

  const invite = useMutation({
    mutationFn: () => inviteMember({ email: email.trim(), role }),
    onSuccess: (u) => {
      setEmail("");
      qc.invalidateQueries({ queryKey: ["team"] });
      setLastLink(inviteLink(u.invite_token));
      copyText(inviteLink(u.invite_token));
    },
    onError: (e) => toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "No se pudo crear la invitación"),
  });

  return (
    <SettingsCard icon={Users} title="Equipo" testid="team-panel"
      description="Invita a tu equipo con un enlace único. Recepción gestiona conversaciones y citas, pero no accede a Configuración.">
      <form className="flex flex-wrap gap-2 mb-5" onSubmit={(e) => { e.preventDefault(); if (email.trim()) invite.mutate(); }}>
        <Input data-testid="invite-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="email@tuclinica.com" className="h-11 flex-1 min-w-[200px]" />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="invite-role-select" className="h-11 w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recepcion" data-testid="invite-role-recepcion">Recepción</SelectItem>
            <SelectItem value="admin" data-testid="invite-role-admin">Administrador</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="invite-submit-btn" type="submit" disabled={invite.isPending} className="h-11 bg-[#1A1A1A] hover:bg-black text-white">
          {invite.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />} Invitar
        </Button>
      </form>
      {lastLink && (
        <div className="mb-5 rounded-xl border border-[#E0D3BC] bg-[#FAF8F5] p-3" data-testid="invite-link-box">
          <div className="text-xs text-[#6B6157] mb-1.5">Enlace de invitación (válido 7 días). Cópialo y envíalo a la persona invitada:</div>
          <div className="flex gap-2">
            <Input readOnly value={lastLink} data-testid="invite-link-input" className="h-9 text-xs bg-white" onFocus={(e) => e.target.select()} />
            <Button type="button" variant="outline" data-testid="invite-link-copy-btn" className="h-9" onClick={() => copyText(lastLink)}>Copiar</Button>
          </div>
        </div>
      )}
      <div className="divide-y divide-[#EDE7DE] rounded-xl border border-[#EDE7DE]" data-testid="team-list">
        {team.map((u) => <TeamMemberRow key={u.id} member={u} isSelf={u.id === currentUser?.id} />)}
      </div>
    </SettingsCard>
  );
};
